/**
 * Iter91.7 — Bloque "Actividad" para FinancePage.
 * Combina:
 *   - KPIs IA 24h (calls, costo USD, tokens, errores)
 *   - Costo IA por feature + por modelo
 *   - Trend diario IA últimos 7d
 *   - Feed unificado de actividad: llamadas IA + ingresos/egresos
 *     con paginación 20/50/100 + export CSV (solo lo que se ve).
 *
 * Recibe `entries` desde FinancePage para no duplicar fetch.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Bot, RefreshCw, AlertTriangle, CheckCircle2, Download,
  ArrowUpCircle, ArrowDownCircle, Plus, Filter, Trash2,
} from "lucide-react";
import { authFetch, API } from "../utils/api";
import { fmtDate, fmtAmt, CATEGORY_LABELS, CURRENCIES } from "./FinancePage.utils";

const TAG_LABEL = {
  site_design: "Diseño del sitio",
  site_code: "Código del sitio",
  web_script: "Textos de venta",
  research: "Investigación",
  agent_v2: "Agente IA (cliente)",
  ast_propose: "AST rápido",
  client_edit: "Edición cliente",
  suggest_style: "Sugerir estilo",
  ai_edit_context: "Edición contextual",
  regenerate_text: "Regenerar texto",
  other: "Otros",
};

const TAG_COLORS = ["#a855f7", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#ec4899", "#eab308"];
const PAGE_SIZES = [20, 50, 100];

function fmtCost(v) {
  if (v == null) return "—";
  if (v >= 1) return `$${v.toFixed(2)}`;
  if (v >= 0.01) return `$${v.toFixed(3)}`;
  return `$${v.toFixed(4)}`;
}

export default function AIUsageSection({ entries = [], className = "", onAddEntry, onDeleteEntry }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(0);
  // Iter92 — filtros internos (ya no hay tabla de transacciones, todo vive acá)
  const [filterKind, setFilterKind] = useState("all");      // all | ai | income | expense
  const [filterCurrency, setFilterCurrency] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Ask for up to 100 AI calls so the feed has volume to paginate locally.
      const r = await authFetch(`${API}/admin/ai-usage?hours=${hours}&limit=100`);
      if (r?.ok) setData(await r.json());
    } catch {} finally { setLoading(false); }
  }, [hours]);

  useEffect(() => {
    load();
    // Iter91.6 — auto-refresh cada 30s para tiempo casi-real
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  // Reset paginación al cambiar filtros / tamaño / ventana
  useEffect(() => { setPage(0); }, [pageSize, hours, filterKind, filterCurrency]);

  // Feed unificado IA + finanzas, ordenado por timestamp desc + filtros
  const activity = useMemo(() => {
    const aiItems = (data?.recent || []).map(r => ({
      kind: "ai",
      ts: r.created_at || r.timestamp || "",
      tag: r.tag,
      model: r.model,
      input_tokens: r.input_tokens,
      output_tokens: r.output_tokens,
      cost_usd: r.cost_usd,
      success: r.success !== false,
    }));
    const finItems = (entries || []).map(e => ({
      id: e.id,
      kind: e.type === "income" ? "income" : "expense",
      ts: e.date || e.created_at || "",
      description: e.description,
      category: e.category,
      amount: Number(e.amount) || 0,
      currency: e.currency,
      source: e.source,
    }));
    let merged = [...aiItems, ...finItems];
    if (filterKind !== "all") merged = merged.filter(r => r.kind === filterKind);
    if (filterCurrency !== "all") merged = merged.filter(r => r.kind === "ai" || r.currency === filterCurrency);
    return merged.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
  }, [data, entries, filterKind, filterCurrency]);

  const totalPages = Math.max(1, Math.ceil(activity.length / pageSize));
  const pageItems = activity.slice(page * pageSize, (page + 1) * pageSize);

  // Iter94 — Total inteligente: ingresos − egresos − costo IA en la ventana.
  // Los egresos `source === "ai_auto"` son generados automáticamente al final
  // de cada generación de sitio (categoría "ai"). Ya están contados en el
  // bloque Costo IA, así que los EXCLUIMOS del bloque Egresos para no sumarlos
  // dos veces.
  const smartTotal = useMemo(() => {
    const incomesByCur = {};
    const expensesByCur = {};
    (entries || []).forEach(e => {
      const cur = e.currency || "UYU";
      const amt = Number(e.amount) || 0;
      if (e.type === "income") {
        incomesByCur[cur] = (incomesByCur[cur] || 0) + amt;
      } else if (e.source !== "ai_auto") {
        expensesByCur[cur] = (expensesByCur[cur] || 0) + amt;
      }
    });
    const aiCostUsd = Number(data?.totals?.cost_usd) || 0;
    const currencies = new Set([...Object.keys(incomesByCur), ...Object.keys(expensesByCur)]);
    const netByCur = {};
    currencies.forEach(c => {
      netByCur[c] = (incomesByCur[c] || 0) - (expensesByCur[c] || 0);
    });
    return { incomesByCur, expensesByCur, aiCostUsd, netByCur };
  }, [entries, data]);

  const exportCSV = () => {
    const rows = [["Tipo", "Fecha", "Descripción", "Detalle", "Tokens", "Monto"]];
    pageItems.forEach(r => {
      if (r.kind === "ai") {
        rows.push([
          "IA", r.ts, TAG_LABEL[r.tag] || r.tag, r.model || "",
          `${r.input_tokens || 0}↑ ${r.output_tokens || 0}↓`,
          fmtCost(r.cost_usd),
        ]);
      } else {
        rows.push([
          r.kind === "income" ? "Ingreso" : "Egreso",
          r.ts, r.description || "", CATEGORY_LABELS[r.category] || r.category || "",
          "", `${r.kind === "income" ? "+" : "-"}${r.amount} ${r.currency || ""}`,
        ]);
      }
    });
    const csv = rows.map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = `actividad-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (!data) {
    return (
      <div className={`rounded-3xl p-8 flex justify-center ${className}`}
           style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.07)" }}>
        <RefreshCw size={20} className="animate-spin text-gray-500" />
      </div>
    );
  }

  const t = data.totals || {};
  const totalTokens = (t.input_tokens || 0) + (t.output_tokens || 0);
  const maxTagCost = Math.max(...(data.by_tag || []).map(r => r.cost_usd), 0.0001);
  const maxModelCost = Math.max(...(data.by_model || []).map(r => r.cost_usd), 0.0001);
  const dailyMax = Math.max(...(data.daily || []).map(d => d.cost_usd), 0.0001);

  return (
    <div className={`rounded-3xl p-6 sm:p-8 space-y-5 ${className}`}
         style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.07)" }}
         data-testid="ai-usage-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(168,85,247,0.12)", border: "1px solid rgba(168,85,247,0.25)" }}>
            <Bot size={16} className="text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-black text-white tracking-tight"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Actividad
            </h2>
            <p className="text-[11px] text-gray-500">
              IA · ingresos · egresos · última {hours}h · refresh 30s
            </p>
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap items-center">
          {onAddEntry && (
            <button
              onClick={onAddEntry}
              data-testid="activity-new-entry-btn"
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10.5px] font-black bg-[#ffd16c] hover:brightness-110 text-black transition-all"
            >
              <Plus size={11} /> Nuevo Registro
            </button>
          )}
          <button
            onClick={() => setShowFilters(s => !s)}
            data-testid="activity-toggle-filters-btn"
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10.5px] font-bold transition-colors ${
              showFilters
                ? "bg-purple-500/20 text-purple-200 border border-purple-500/40"
                : "bg-white/[0.06] text-gray-400 border border-white/[0.08] hover:bg-white/[0.10]"
            }`}
          >
            <Filter size={10} /> Filtros
          </button>
          {[24, 168, 720].map(h => (
            <button
              key={h}
              onClick={() => setHours(h)}
              className={`px-2.5 py-1 rounded-lg text-[10.5px] font-bold transition-colors ${
                hours === h ? "bg-purple-500 text-white" : "bg-white/[0.06] text-gray-400 hover:bg-white/[0.10]"
              }`}
              data-testid={`ai-usage-window-${h}`}
            >
              {h === 24 ? "24h" : h === 168 ? "7d" : "30d"}
            </button>
          ))}
          <button onClick={load} className="px-2 py-1 rounded-lg bg-white/[0.06] text-gray-400 hover:bg-white/[0.10]"
                  data-testid="ai-usage-refresh-btn">
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Filtros expandibles */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 rounded-xl"
             style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}
             data-testid="activity-filters-panel">
          <div className="flex gap-1">
            {[
              { id: "all",      label: "Todo" },
              { id: "ai",       label: "IA" },
              { id: "income",   label: "Ingresos" },
              { id: "expense",  label: "Egresos" },
            ].map(c => (
              <button
                key={c.id}
                onClick={() => setFilterKind(c.id)}
                data-testid={`activity-filter-${c.id}`}
                className={`px-2.5 py-1 rounded-md text-[10px] font-black transition-colors ${
                  filterKind === c.id
                    ? "bg-purple-500/20 text-purple-200 border border-purple-500/40"
                    : "bg-white/[0.04] text-gray-400 border border-white/[0.06] hover:bg-white/[0.08]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <select
            value={filterCurrency}
            onChange={e => setFilterCurrency(e.target.value)}
            data-testid="activity-filter-currency"
            className="bg-white/[0.04] border border-white/[0.06] rounded-md px-2 py-1 text-[10px] font-bold text-gray-300 outline-none"
          >
            <option value="all">Todas las monedas</option>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {(filterKind !== "all" || filterCurrency !== "all") && (
            <button
              onClick={() => { setFilterKind("all"); setFilterCurrency("all"); }}
              className="text-[10px] font-bold text-gray-500 hover:text-white transition-colors ml-1"
            >
              Limpiar
            </button>
          )}
        </div>
      )}

      {/* KPIs IA */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ai-usage-kpis">
        {[
          { label: "Llamadas IA", value: t.calls || 0, color: "text-white" },
          { label: "Costo IA", value: fmtCost(t.cost_usd), color: "text-purple-300" },
          { label: "Tokens", value: totalTokens.toLocaleString("es-UY"), color: "text-gray-200" },
          { label: "Errores", value: t.errors || 0, color: (t.errors || 0) > 0 ? "text-red-300" : "text-emerald-300" },
        ].map((k, i) => (
          <div key={i} className="p-3 rounded-2xl"
               style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <p className="text-[9.5px] font-black text-gray-500 uppercase tracking-widest mb-1.5">{k.label}</p>
            <p className={`text-xl sm:text-2xl font-black ${k.color} truncate`}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Daily trend IA */}
      {data.daily && data.daily.length > 0 && (
        <div className="p-4 rounded-2xl"
             style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">Costo IA diario · 7d</p>
          <div className="flex items-end gap-2 h-24" data-testid="ai-usage-daily">
            {data.daily.map((d, i) => {
              const h = (d.cost_usd / dailyMax) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1.5 group">
                  <div className="flex-1 w-full flex items-end">
                    <div className="w-full rounded-t-md bg-purple-500/40 group-hover:bg-purple-400/70 transition-colors"
                         style={{ height: `${Math.max(2, h)}%` }} />
                  </div>
                  <span className="text-[9px] font-bold text-gray-500">{d.date.slice(5)}</span>
                  <span className="text-[10px] font-bold text-purple-300">{fmtCost(d.cost_usd)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* By tag + by model */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="p-4 rounded-2xl"
             style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">Costo IA por feature</p>
          {(data.by_tag || []).length === 0 ? (
            <p className="text-[11px] text-gray-500 italic">Sin datos</p>
          ) : (
            <div className="space-y-2" data-testid="ai-usage-by-tag">
              {data.by_tag.map((row, i) => {
                const w = (row.cost_usd / maxTagCost) * 100;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-0.5 text-[11px]">
                      <span className="font-bold text-gray-200">{TAG_LABEL[row.tag] || row.tag}</span>
                      <span className="font-mono text-purple-300">
                        {fmtCost(row.cost_usd)} <span className="text-gray-600">· {row.calls}</span>
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <div className="h-full" style={{ width: `${Math.max(2, w)}%`, background: TAG_COLORS[i % TAG_COLORS.length] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 rounded-2xl"
             style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
          <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-3">Costo IA por modelo</p>
          {(data.by_model || []).length === 0 ? (
            <p className="text-[11px] text-gray-500 italic">Sin datos</p>
          ) : (
            <div className="space-y-2" data-testid="ai-usage-by-model">
              {data.by_model.slice(0, 6).map((row, i) => {
                const w = (row.cost_usd / maxModelCost) * 100;
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-0.5 text-[11px]">
                      <span className="font-mono text-gray-200 truncate max-w-[60%]" title={row.model}>{row.model}</span>
                      <span className="font-mono text-purple-300">
                        {fmtCost(row.cost_usd)} <span className="text-gray-600">· {row.calls}</span>
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <div className="h-full bg-purple-400" style={{ width: `${Math.max(2, w)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Feed unificado: IA + ingresos + egresos */}
      <div>
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
            Actividad reciente · {activity.length}
          </p>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold text-gray-500 mr-1">Mostrar</span>
            {PAGE_SIZES.map(s => (
              <button
                key={s}
                onClick={() => setPageSize(s)}
                data-testid={`activity-page-size-${s}`}
                className={`px-2 py-1 rounded-md text-[10px] font-black transition-colors ${
                  pageSize === s
                    ? "bg-purple-500/20 text-purple-200 border border-purple-500/40"
                    : "bg-white/[0.04] text-gray-400 border border-white/[0.06] hover:bg-white/[0.08]"
                }`}
              >
                {s}
              </button>
            ))}
            <button
              onClick={exportCSV}
              data-testid="activity-export-btn"
              className="ml-1 flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-black bg-white/[0.06] hover:bg-white/[0.10] text-gray-300 border border-white/[0.08]"
            >
              <Download size={10} /> CSV
            </button>
          </div>
        </div>

        {pageItems.length === 0 ? (
          <p className="text-[11px] text-gray-500 italic px-3 py-4">Sin actividad en este período</p>
        ) : (
          <div className="space-y-1.5" data-testid="activity-feed">
            {pageItems.map((r, i) => (
              <ActivityRow
                key={`${r.kind}-${r.ts}-${r.id || i}`}
                row={r}
                onDelete={onDeleteEntry}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 text-[10.5px]">
            <span className="text-gray-500 font-bold">
              Página {page + 1} de {totalPages}
            </span>
            <div className="flex gap-1.5">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                data-testid="activity-prev-btn"
                className="px-2.5 py-1 rounded-md font-black bg-white/[0.06] hover:bg-white/[0.10] text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                ←
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                data-testid="activity-next-btn"
                className="px-2.5 py-1 rounded-md font-black bg-white/[0.06] hover:bg-white/[0.10] text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Iter93 — Total Inteligente (resumen de la ventana) */}
      <div
        className="rounded-2xl p-4 grid grid-cols-2 lg:grid-cols-4 gap-3"
        style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
        data-testid="smart-total-row"
      >
        {/* Ingresos */}
        <div className="space-y-1" data-testid="smart-total-income">
          <p className="text-[9.5px] font-black text-emerald-300/70 uppercase tracking-widest">Ingresos · {hours}h</p>
          {Object.entries(smartTotal.incomesByCur).length === 0 ? (
            <p className="text-sm font-bold text-gray-600">—</p>
          ) : Object.entries(smartTotal.incomesByCur).map(([cur, v]) => (
            <p key={cur} className="text-base sm:text-lg font-black text-emerald-300 tabular-nums">
              +{fmtAmt(v, cur)}
            </p>
          ))}
        </div>
        {/* Egresos */}
        <div className="space-y-1" data-testid="smart-total-expense">
          <p className="text-[9.5px] font-black text-orange-300/70 uppercase tracking-widest">Egresos · {hours}h</p>
          {Object.entries(smartTotal.expensesByCur).length === 0 ? (
            <p className="text-sm font-bold text-gray-600">—</p>
          ) : Object.entries(smartTotal.expensesByCur).map(([cur, v]) => (
            <p key={cur} className="text-base sm:text-lg font-black text-orange-300 tabular-nums">
              −{fmtAmt(v, cur)}
            </p>
          ))}
        </div>
        {/* Costo IA */}
        <div className="space-y-1" data-testid="smart-total-ai">
          <p className="text-[9.5px] font-black text-purple-300/70 uppercase tracking-widest">Costo IA · {hours}h</p>
          <p className="text-base sm:text-lg font-black text-purple-300 tabular-nums">
            −{fmtCost(smartTotal.aiCostUsd)} USD
          </p>
          <p className="text-[9.5px] text-gray-500 font-bold">{(data?.totals?.calls || 0)} llamadas</p>
        </div>
        {/* Neto */}
        <div className="space-y-1" data-testid="smart-total-net">
          <p className="text-[9.5px] font-black text-gray-400 uppercase tracking-widest">Neto · {hours}h</p>
          {Object.entries(smartTotal.netByCur).length === 0 && smartTotal.aiCostUsd === 0 ? (
            <p className="text-sm font-bold text-gray-600">Sin movimientos</p>
          ) : (
            <>
              {Object.entries(smartTotal.netByCur).map(([cur, v]) => {
                const isPositive = v >= 0;
                return (
                  <div key={cur} className="flex items-center gap-1.5">
                    <span
                      className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                        isPositive
                          ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                          : "bg-red-500/15 text-red-300 border border-red-500/30"
                      }`}
                    >
                      {isPositive ? "+" : "−"}
                    </span>
                    <p className={`text-base sm:text-lg font-black tabular-nums ${
                      isPositive ? "text-emerald-300" : "text-red-300"
                    }`}>
                      {fmtAmt(Math.abs(v), cur)}
                    </p>
                  </div>
                );
              })}
              {smartTotal.aiCostUsd > 0 && (
                <p className="text-[9.5px] text-gray-500 font-mono">
                  ↳ neto IA: {fmtCost(smartTotal.aiCostUsd)} USD aparte
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-component ──────────────────────────────────────────────────────────

function ActivityRow({ row, onDelete }) {
  if (row.kind === "ai") {
    return (
      <div className="flex items-center gap-2 p-2 rounded-lg text-[10.5px] font-mono"
           style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
        {row.success
          ? <CheckCircle2 size={11} className="text-emerald-400 flex-shrink-0" />
          : <AlertTriangle size={11} className="text-red-400 flex-shrink-0" />}
        <span className="text-purple-300 font-bold w-28 truncate">IA · {TAG_LABEL[row.tag] || row.tag}</span>
        <span className="text-gray-300 flex-1 truncate hidden sm:inline">{row.model}</span>
        <span className="text-gray-400 hidden md:inline">{row.input_tokens || 0}↑ {row.output_tokens || 0}↓</span>
        <span className="text-purple-200 font-bold w-16 text-right">{fmtCost(row.cost_usd)}</span>
        <span className="text-gray-600 w-20 text-right hidden sm:inline">{fmtDate(row.ts)}</span>
      </div>
    );
  }
  const isIncome = row.kind === "income";
  // Stripe income rows are immutable (auto-synced from payments)
  const canDelete = !!onDelete && row.id && row.source !== "stripe";
  return (
    <div className="group flex items-center gap-2 p-2 rounded-lg text-[10.5px] font-mono"
         style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}
         data-testid={`activity-row-${row.id || row.ts}`}>
      {isIncome
        ? <ArrowUpCircle size={11} className="text-emerald-400 flex-shrink-0" />
        : <ArrowDownCircle size={11} className="text-orange-400 flex-shrink-0" />}
      <span className={`font-bold w-28 truncate ${isIncome ? "text-emerald-300" : "text-orange-300"}`}>
        {isIncome ? "Ingreso" : "Egreso"} · {(CATEGORY_LABELS[row.category] || row.category || "—").slice(0, 14)}
      </span>
      <span className="text-gray-200 flex-1 truncate">{row.description || "—"}</span>
      <span className={`font-bold w-20 text-right ${isIncome ? "text-emerald-200" : "text-orange-200"}`}>
        {isIncome ? "+" : "−"}{fmtAmt(row.amount, row.currency)}
      </span>
      <span className="text-gray-600 w-20 text-right hidden sm:inline">{fmtDate(row.ts)}</span>
      {canDelete && (
        <button
          onClick={() => onDelete(row.id)}
          data-testid={`activity-row-delete-${row.id}`}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-500/20 text-gray-500 hover:text-red-400"
          title="Eliminar registro"
        >
          <Trash2 size={10} />
        </button>
      )}
    </div>
  );
}
